import { useMemo } from 'react';

import { AbsenceWithTypeData, checkIsOverLimitAndNotAllowed } from '@/domain/requestsPage';
import { DELETION_STATUS, REQUEST_STATUS } from '@/utils/enums/requestsPage';

interface UseRequestDetailsStateResult {
    isAccepted: boolean;
    isWaitingForAcceptance: boolean;
    isWaitingForDeletion: boolean;
    isOwnRequest: boolean;
    showDeletionButton: boolean;
    showAcceptanceButtons: boolean;
    showWaitingDeletionButton: boolean;
    allButtonsShown: boolean;
    isOverLimitAndNotAllowed: boolean;
    isOverLimitAndAllowed: boolean;
}

const useRequestDetailsState = (
    singleRequestDetails: AbsenceWithTypeData,
    credentialId: string
): UseRequestDetailsStateResult => {
    return useMemo(() => {
        const { status, statusInRequest, userDto, hasPermissionForAcceptance, allowOverbooking, isOverLimit } =
            singleRequestDetails;

        const isAccepted = status === REQUEST_STATUS.ACCEPTED;
        const isWaitingForAcceptance = status === REQUEST_STATUS.NEW;
        const isWaitingForDeletion = statusInRequest === DELETION_STATUS.DELETION_REQUEST;
        const isOwnRequest = credentialId === userDto.credentialId;

        const showDeletionButton = !isWaitingForDeletion && (isAccepted || isWaitingForAcceptance) && isOwnRequest;
        const showAcceptanceButtons = isWaitingForAcceptance && hasPermissionForAcceptance;
        const showWaitingDeletionButton = isWaitingForDeletion && hasPermissionForAcceptance;
        const allButtonsShown = (showDeletionButton || showWaitingDeletionButton) && showAcceptanceButtons;
        const isOverLimitAndNotAllowed = checkIsOverLimitAndNotAllowed(singleRequestDetails);
        const isOverLimitAndAllowed = Boolean(isOverLimit && allowOverbooking);

        return {
            isAccepted,
            isWaitingForAcceptance,
            isWaitingForDeletion,
            isOwnRequest,
            showDeletionButton,
            showAcceptanceButtons,
            showWaitingDeletionButton,
            allButtonsShown,
            isOverLimitAndNotAllowed,
            isOverLimitAndAllowed,
        };
    }, [singleRequestDetails, credentialId]);
};

export default useRequestDetailsState;
